import React from 'react';
import path from 'path';
import { promises as fs } from 'fs';
import { NextSeo } from 'next-seo';
import PropTypes from 'prop-types';
import { CurrentItemPage } from '../../components/Views/CurrentItemPage';
import { Layout } from '../../components/Layout/Layout';
import { makeHostSum, normalizePath } from '../../utils/helpers/url';
import initApollo from '../../utils/init-Apollo';
import { getLocaleOnSSR } from '../../utils/helpers/getLocaleOnSSR';
import {
  getCurrentItemData,
  getResStatusCode,
  normalizeItemPagePath
} from '../../utils/helpers/item';
import { generateItemSeoObj } from '../../utils/helpers/seo';
import { resolveCurrentItemMockFile } from '../../utils/helpers/resolveCurrentItemMockFile';
import { shouldUseMock } from '../../utils/helpers/shouldUseMock';
import { withBugsnagGetServerSideProps } from '../../utils/withBugsnag';
import { extractApolloState } from '../../utils/helpers/extractApolloState';

const CurrentItem = props => {
  const mainPageImage = props?.mainPageImage?.full;
  const isSoftError = props?.isSoftError;

  return (
    <>
      {(mainPageImage || isSoftError) && (
        <NextSeo
          noindex={isSoftError}
          nofollow={false}
          robotsProps={
            mainPageImage
              ? { maxImagePreview: isSoftError ? 'none' : 'large' }
              : {}
          }
        />
      )}
      <CurrentItemPage {...props} />
    </>
  );
};

CurrentItem.propTypes = {
  mainPageImage: PropTypes.shape({
    full: PropTypes.string
  }),
  isSoftError: PropTypes.bool,
  ssrVersion: PropTypes.string,
  itemId: PropTypes.string,
  currentItemData: PropTypes.object
};

const getCurrentItemMockData = async (params, query) => {
  const filePath = resolveCurrentItemMockFile(params.id, query);
  const currentData = path.join(
    process.cwd(),
    '__mocks__/fixture/current_item',
    filePath
  );

  const file = await fs.readFile(currentData, 'utf-8');
  const raw = JSON.parse(file);
  return params.id === '3878205'
    ? {
        ...raw,
        data: {
          ...raw.data,
          item: {
            ...raw.data.item,
            isAuthor: false,
            isMine: false
          }
        }
      }
    : raw;
};

export const getServerSideProps = withBugsnagGetServerSideProps(
  async ({ res, req, params, query }) => {
    if (shouldUseMock(query)) {
      const mock = await getCurrentItemMockData(params, query);
      return {
        props: {
          currentItemData: mock.data.item,
          mainPageImage: mock.data.item?.photos?.[0] || {}
        }
      };
    }

    const currentLang = getLocaleOnSSR(req);
    const apolloClient = initApollo({}, { lang: currentLang });

    const currentAsPath = normalizeItemPagePath(req.url);
    const newAsPath = normalizePath(currentAsPath);
    const hostSum = makeHostSum({ asPath: newAsPath, req });
    const itemId = params.id;

    const show404Page = response => {
      response.statusCode = 404;
      return { props: { statusCode: 404 } };
    };

    if (res && !currentAsPath.match(/\d+/)?.[0]) return show404Page(res);

    const currentItemRes = await getCurrentItemData(apolloClient, itemId);

    const { currentItemData, errorCode } = currentItemRes;

    const seoData = generateItemSeoObj(currentItemData, newAsPath, hostSum);

    const statusCode = getResStatusCode(currentItemData);
    const isSoftError = !!statusCode || !!errorCode || false;

    res.setHeader(
      'Cache-Control',
      isSoftError
        ? 'no-store, no-cache, must-revalidate, max-age=0'
        : 'public, max-age=600, s-maxage=600, stale-while-revalidate=86400'
    );

    return extractApolloState(apolloClient, {
      props: {
        newAsPath,
        hostSum,
        itemId,
        seoData,
        mainPageImage: currentItemData?.photos?.[0] || {},
        currentLang,
        isSoftError,
        ...currentItemRes
      }
    });
  },
  '/items/[id]'
);

CurrentItem.getLayout = (page, pageProps) => (
  <Layout
    isSafeAreaTop
    isBottomNavigation={false}
    isCurrentItemPage
    isBanner
    pageProps={pageProps}
    isBackgroundBanner
    hideMobileHeader
  >
    {page}
  </Layout>
);

export default CurrentItem;
